// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { replaceInChain } from "@insite/client-framework/HandlerCreator";
import logger from "@insite/client-framework/Logger";
import {
    chain,
    ProcessFileResult,
    ProcessUploadedFile,
} from "@insite/client-framework/Store/Components/OrderUpload/Handlers/ProcessFile";
import { WorkBook } from "xlsx";

export const XngProcessUploadedFile: any = async (props: any) => {
    if (props.parameter.fileExtension === "xls" || props.parameter.fileExtension === "xlsx") {
        const XLSX = await import(/* webpackChunkName: "xlsx" */ "xlsx");
        const data = fixData(props.parameter.data);
        try {
            const workbook = XLSX.read(btoa(data), { type: "base64" });
            if (workbook) {
                await processWorkBook(workbook, props.parameter.firstRowHeading, props.result);
            }
        } catch (error) {
            logger.error(error);
            props.result.isBadFile = true;
        }
    } else {
        await ProcessUploadedFile(props);
    }
    if (!props.result.isBadFile && !props.result.uploadLimitExceeded) {
        props.result.allowCancel = true;
        props.result.isUploading = true;
    }
};

// XNG-Change overrinding this because it's not exported on ootb file - nothing changed
const fixData = (data: ArrayBuffer) => {
    let o = "";
    let l = 0;
    const w = 10240;
    for (; l < data.byteLength / w; ++l) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        o += String.fromCharCode.apply(null, new Uint8Array(data.slice(l * w, l * w + w)) as any);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    o += String.fromCharCode.apply(null, new Uint8Array(data.slice(l * w)) as any);

    return o;
};

const processWorkBook = async (workBook: WorkBook, firstRowHeading: boolean, result: ProcessFileResult) => {
    const XLSX = await import(/* webpackChunkName: "xlsx" */ "xlsx");

    result.uploadedItems = [];
    workBook.SheetNames.forEach((sheetName: string) => {
        const opts = {
            header: 1,
        };

        let rows = XLSX.utils.sheet_to_json<string>(workBook.Sheets[sheetName], opts);
        if (rows.length > 0) {
            if (firstRowHeading) {
                rows = rows.slice(1, rows.length);
            }

            // XNG-Change Avoid filtering numbers. only filter if the cell is empty.
            rows = rows.filter(row => row[0] != null && row[0].toString().length > 0);
            if (limitExceeded(rows.length, result)) {
                return;
            }

            result.uploadedItems = rows.map(row => ({
                // XNG-Change
                name: row[0].toString(),
                qtyOrdered: parseFloat(row[1]) ? parseFloat(row[1]) : undefined,
                unitOfMeasure: row[2],
            }));
        }
    });
};

// XNG-Change overrinding this because it's not exported on ootb file - nothing changed
const limitExceeded = (rowsCount: number, result: ProcessFileResult) => {
    const maximumAllowedRowsCount = 500;
    result.uploadLimitExceeded = rowsCount > maximumAllowedRowsCount;
    return result.uploadLimitExceeded;
};

replaceInChain(chain, ProcessUploadedFile, XngProcessUploadedFile);
